import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { serviceOrdersAPI, studentsAPI } from '../services/api';
import { FiRefreshCw, FiAlertTriangle, FiShoppingBag, FiPlus, FiX, FiTrash2, FiEdit2 } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

const SERVICE_TYPES = [
  ['TRANSLATION', 'Sworn Translation'],
  ['INSURANCE', 'Health Insurance'],
  ['IKAMET', 'Residence Permit (İkamet)'],
  ['HOUSING', 'Housing Placement'],
  ['OTHER', 'Other'],
];

const STATUSES = [
  ['SUGGESTED', 'Suggested', '#94a3b8'],
  ['PENDING', 'Pending', '#f5a623'],
  ['IN_PROGRESS', 'In Progress', '#4facfe'],
  ['COMPLETED', 'Completed', '#43e97b'],
  ['CANCELLED', 'Cancelled', '#f5576c'],
];
const statusColor = (s) => (STATUSES.find((x) => x[0] === s) || [null, null, '#666'])[2];
const statusLabel = (s) => (STATUSES.find((x) => x[0] === s) || [null, s])[1];
const money = (v) => (v === null || v === undefined || v === '' ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const OrderModal = ({ vendors, initial, onSave, onCancel, loading }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    student: initial?.student || '',
    service_type: initial?.service_type || 'TRANSLATION',
    status: initial?.status || 'PENDING',
    vendor: initial?.vendor || '',
    price: initial?.price ?? '',
    cost: initial?.cost ?? '',
    notes: initial?.notes || '',
  });
  const [studentQuery, setStudentQuery] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (isEdit || studentQuery.trim().length < 2) { setStudentResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await studentsAPI.list({ search: studentQuery.trim(), page_size: 20 });
        if (!cancelled) setStudentResults(res.data.results || res.data || []);
      } catch { if (!cancelled) setStudentResults([]); }
      finally { if (!cancelled) setSearching(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [studentQuery, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSave = form.student && form.service_type && form.status && !loading;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 480 }}>
        <div className="modal-header"><h2 style={{ fontSize: 18 }}>{isEdit ? 'Edit Service Order' : 'New Service Order'}</h2></div>
        <div className="modal-form">
          {isEdit ? (
            <div className="form-group">
              <label>Student</label>
              <div style={{ color: '#666', padding: '10px 0' }}>{initial.student_name} ({initial.student_reference})</div>
            </div>
          ) : (
            <div className="form-group">
              <label>Student *</label>
              {form.student ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: 8, padding: 10 }}>
                  <span>{studentResults.find((s) => s.id === form.student)?.full_name || `Student #${form.student}`}</span>
                  <button onClick={() => set('student', '')} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}><FiX size={14} /></button>
                </div>
              ) : (
                <>
                  <input value={studentQuery} onChange={(e) => setStudentQuery(e.target.value)} placeholder="Type a name, email or ID…" />
                  {searching && <div style={{ color: '#94a3b8', fontSize: 12 }}>Searching…</div>}
                  {studentResults.length > 0 && (
                    <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, maxHeight: 160, overflowY: 'auto' }}>
                      {studentResults.map((s) => (
                        <div key={s.id} onClick={() => { set('student', s.id); setStudentResults([s]); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>
                          {s.full_name || `${s.first_name} ${s.last_name}`} <span style={{ color: '#94a3b8' }}>· {s.student_id}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Service *</label>
              <select value={form.service_type} onChange={(e) => set('service_type', e.target.value)}>
                {SERVICE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Status *</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value)}>
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Vendor</label>
            <select value={form.vendor} onChange={(e) => set('vendor', e.target.value)}>
              <option value="">— none —</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Price — student pays (USD)</label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Cost — paid to vendor (USD)</label>
              <input type="number" min="0" step="0.01" value={form.cost} onChange={(e) => set('cost', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} />
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            className="btn-primary"
            onClick={() => onSave({
              ...(isEdit ? {} : { student: form.student }),
              service_type: form.service_type,
              status: form.status,
              vendor: form.vendor ? parseInt(form.vendor, 10) : null,
              price: form.price === '' ? null : form.price,
              cost: form.cost === '' ? null : form.cost,
              notes: form.notes,
            })}
            disabled={!canSave}
          >
            {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Order'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ServiceOrders = () => {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(null); // null | {} (new) | order (edit)
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [oRes, vRes, sRes] = await Promise.all([
        serviceOrdersAPI.list(statusFilter ? { status: statusFilter } : {}),
        serviceOrdersAPI.vendors.list().catch(() => ({ data: [] })),
        serviceOrdersAPI.revenueSummary().catch(() => ({ data: null })),
      ]);
      setOrders(oRes.data.results || oRes.data || []);
      setVendors(vRes.data.results || vRes.data || []);
      setSummary(sRes.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load service orders.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    setActionLoading(true);
    try {
      if (modal?.id) await serviceOrdersAPI.update(modal.id, data);
      else await serviceOrdersAPI.create(data);
      setModal(null);
      load();
    } catch (e) {
      setError(JSON.stringify(e?.response?.data) || 'Failed to save order.');
    } finally {
      setActionLoading(false);
    }
  };

  const advance = async (order, status) => {
    try {
      await serviceOrdersAPI.update(order.id, { status });
      load();
    } catch {
      setError('Failed to update status.');
    }
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Delete the ${statusLabel(order.status)} "${order.service_type_display}" order for ${order.student_name}?`)) return;
    try {
      await serviceOrdersAPI.delete(order.id);
      load();
    } catch {
      setError('Failed to delete order.');
    }
  };

  const totalRevenue = summary?.by_service_type?.reduce((a, r) => a + (r.total_revenue || 0), 0) ?? 0;

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiShoppingBag size={22} color="#011ff3" /> Service Orders
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
            Add-on services sold per student — translation, insurance, İkamet, housing. Orders auto-suggested by quest triggers land here as "Suggested".
          </p>
        </div>
        <div className="header-actions-right">
          <button className="btn-primary" onClick={() => setModal({})}>
            <FiPlus size={15} /> New Order
          </button>
          <button className="btn-icon" onClick={load} title="Refresh">
            <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
          </button>
        </div>
      </div>

      {summary && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div className="cv-subsection" style={{ minWidth: 150, margin: 0 }}>
            <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Revenue (completed)</div>
            <div style={{ color: '#43e97b', fontSize: 20, fontWeight: 700, marginTop: 4 }}>{money(totalRevenue)}</div>
          </div>
          {STATUSES.filter((s) => ['SUGGESTED', 'PENDING', 'IN_PROGRESS'].includes(s[0])).map(([code, lbl, col]) => (
            <div key={code} className="cv-subsection" style={{ minWidth: 120, margin: 0 }}>
              <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{lbl}</div>
              <div style={{ color: col, fontSize: 20, fontWeight: 700, marginTop: 4 }}>{summary.orders_by_status?.[code] ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[['', 'All'], ...STATUSES.map((s) => [s[0], s[1]])].map(([v, l]) => (
          <button key={v || 'all'} onClick={() => setStatusFilter(v)} className="status-pill"
            style={{ cursor: 'pointer', color: statusFilter === v ? '#4338ca' : '#94a3b8', background: statusFilter === v ? 'rgba(1, 31, 243,0.1)' : '#fff', borderColor: statusFilter === v ? '#011ff3' : '#e0e0e0' }}>
            {l}
          </button>
        ))}
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <FiAlertTriangle size={18} />
          <span style={{ flex: 1, wordBreak: 'break-word' }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="empty-state">No service orders{statusFilter ? ` with status "${statusLabel(statusFilter)}"` : ''}.</div>
        ) : (
          <table className="students-table" style={{ minWidth: 860 }}>
            <thead>
              <tr>
                {['Student', 'Service', 'Status', 'Vendor', 'Price', 'Cost', 'Revenue', ''].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td>
                    <strong>{o.student_name}</strong>
                    <div style={{ color: '#94a3b8', fontSize: 11 }}>{o.student_reference}</div>
                  </td>
                  <td>
                    {o.service_type_display}
                    {o.triggered_by_quest ? <div style={{ color: '#94a3b8', fontSize: 11 }}>via {o.triggered_by_quest}</div> : null}
                  </td>
                  <td>
                    <select value={o.status} onChange={(e) => advance(o, e.target.value)}
                      style={{ border: '1px solid ' + statusColor(o.status), color: statusColor(o.status), borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: '#fff' }}>
                      {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td>{o.vendor_name || '—'}</td>
                  <td>{money(o.price)}</td>
                  <td>{money(o.cost)}</td>
                  <td style={{ color: o.revenue > 0 ? '#43e97b' : '#94a3b8', fontWeight: 600 }}>{money(o.revenue)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button onClick={() => setModal(o)} title="Edit" className="btn-icon" style={{ marginRight: 6 }}><FiEdit2 size={14} /></button>
                    <button onClick={() => handleDelete(o)} title="Delete" className="btn-icon"><FiTrash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && createPortal(
        <OrderModal
          vendors={vendors}
          initial={modal.id ? modal : null}
          onSave={handleSave}
          onCancel={() => setModal(null)}
          loading={actionLoading}
        />,
        document.body
      )}
    </div>
  );
};

export default ServiceOrders;
